import firebase from "firebase/app";
import "firebase/auth";
import { useEffect } from "react";
import { useSelector, useStore } from "react-redux";

import firestore from "src/apiService/firestore";
import type { AppState } from "src/store";
import { userClaimsSelector } from "src/store/system/selector";
import { bounceImpersonationSession } from "src/utils/impersonationRevocation";

/**
 * Watches the live impersonation grant doc. When the reseller revokes access the
 * backend deletes Support_Users/{support_uid}/Tokens/{clientUid}; this listener
 * fires immediately so the accountant is bounced without waiting for a callable.
 */
function ImpersonationGrantWatcher(): null {
  const store = useStore<AppState>();
  const claims = useSelector(userClaimsSelector);
  const supportUid = claims.support_uid;

  useEffect(() => {
    if (typeof supportUid !== "string" || !supportUid) return;

    const clientUid = firebase.auth().currentUser?.uid;
    if (!clientUid) return;

    const grantRef = firestore()
      .collection("Support_Users")
      .doc(supportUid)
      .collection("Tokens")
      .doc(clientUid);

    const unsubscribe = grantRef.onSnapshot(
      (snap) => {
        if (!snap.exists) {
          bounceImpersonationSession(store);
        }
      },
      (error: firebase.firestore.FirestoreError) => {
        if (error.code === "permission-denied") {
          bounceImpersonationSession(store);
        }
      }
    );

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [supportUid, store]);

  return null;
}

export default ImpersonationGrantWatcher;
